// @flow
import { has, isPlainObject } from "../helpers/objects";

import {
  IllegalArgumentException,
  UnauthorizedException,
  FetchException,
  TimeoutException,
  NotFoundException,
  JsonParseException,
  NetworkException,
} from "../../exceptions";

import Cache from "../browser/Cache";

import { HTTP_METHODS } from "../../constants/Constants";

import { getSetting } from "../../constants/Settings";

import type { RequestOptions } from "./types";

const NETWORK_ERROR_CODE = 0;
const SUCCESS_CODE = 200;
const CLIENT_ERROR_CODE = 400;
const UNAUTHORIZED_CODE = 401;
const REDIRECTION_CODE = 300;
const NOT_FOUND_CODE = 404;

const JSON_TYPE = "application/json";

/**
 * @private
 */
class XHR {
  _options: RequestOptions;
  _xmlhttp: XMLHttpRequest;
  _attempts: number;

  /**
   */
  constructor(options: RequestOptions) {
    this._options = options;
    this._attempts = 0;
  }

  /**
   */
  get xmlhttp(): XMLHttpRequest {
    return this._xmlhttp;
  }

  /**
   */
  set xmlhttp(xmlhttp: XMLHttpRequest) {
    this._xmlhttp = xmlhttp;
  }

  /**
   */
  get options(): RequestOptions {
    return this._options;
  }

  /**
   */
  set options(options: RequestOptions) {
    this._options = options;
  }

  /**
   */
  get attempts(): number {
    return this._attempts;
  }

  /**
   */
  set attempts(attempts: number) {
    this._attempts = attempts;
  }

  /**
   * Create and fix url when params or both querystring and params exist.
   */
  get url(): string {
    const { url } = this.options;
    if (this.params !== "") {
      if (url.includes("?")) {
        const aUrl = url.split("?");

        return `${aUrl[0]}?${aUrl[1]}&${this.params}`;
      }

      return `${url}?${this.params}`;
    }

    return url;
  }

  /**
   */
  get params(): string {
    return this.options.params || "";
  }

  /**
   */
  get data(): any | null {
    return this.options.data || null;
  }

  /**
   */
  get method(): $Keys<typeof HTTP_METHODS> {
    return this.options.method || HTTP_METHODS.GET;
  }

  /**
   */
  get timeout(): number {
    return this.options.timeout || getSetting("XHR_TIMEOUT_MS", 300000);
  }

  /**
   */
  get responseType(): string {
    return this.options.responseType || "json";
  }

  /**
   */
  get onProgress(): ProgressEventHandler {
    if (this.options.onProgress) {
      return this.options.onProgress;
    }

    return () => {
      // do nothing
    };
  }

  /**
   */
  get events(): { [eventName: string]: () => void } {
    return this.options.events || {};
  }

  /**
   */
  getBaseLocale(): string {
    // $FlowIssue
    return globalThis.navigator?.language?.split("-")[0] || "en";
  }

  /**
   */
  get headers(): { [string]: string } {
    const baseHeaders = {
      Accept: JSON_TYPE,
      "Content-Type": JSON_TYPE,

      // needed for csrf, see: https://plaza.beinformed.com/amdoc/csrf-protection-in-modular-ui-services-40054554.html
      "X-Requested-With": "XMLHttpRequest",

      "Accept-Language": this.getBaseLocale(),
    };

    if (this.options.headers) {
      // $FlowFixMe[cannot-spread-indexer]
      return {
        ...baseHeaders,
        ...this.options.headers,
      };
    }

    return baseHeaders;
  }

  /**
   */
  timeoutHandler(reject: Function): any {
    return reject(new TimeoutException(this.url, this.method));
  }

  /**
   */
  errorHandler(reject: Function, responseJSON: any = null): any {
    const { status } = this.xmlhttp;
    const { isReload } = this.options;

    if (status === NETWORK_ERROR_CODE) {
      return reject(new NetworkException(this.url, this.method));
    }

    if (status === NOT_FOUND_CODE) {
      return reject(new NotFoundException(this.url, this.method, isReload));
    }

    if (status === UNAUTHORIZED_CODE) {
      return reject(
        new UnauthorizedException(this.url, this.method, responseJSON),
      );
    }

    return reject(new FetchException(responseJSON, this.xmlhttp, this.options));
  }

  /**
   */
  getResponse(): any {
    const { responseType, response } = this.xmlhttp;

    if (
      (responseType === "" || responseType === "text") &&
      this.getResponseHeader("Content-Type") === JSON_TYPE
    ) {
      if (isPlainObject(response)) {
        return response;
      }

      try {
        return JSON.parse(response);
      } catch (error) {
        throw new JsonParseException(error);
      }
    }

    return response;
  }

  /**
   */
  responseHandler(resolve: Function, reject: Function): any {
    const { status } = this.xmlhttp;

    let response = null;
    try {
      response = this.getResponse();
    } catch (error) {
      return reject(error);
    }

    if (
      (status >= SUCCESS_CODE && status < REDIRECTION_CODE) ||
      (status === CLIENT_ERROR_CODE && has(response, "formresponse"))
    ) {
      return resolve(response);
    }

    return this.errorHandler(reject, response);
  }

  /**
   */
  setEventListeners(resolve: Function, reject: Function) {
    if (this.options.onProgress) {
      ["load", "loadend", "loadstart", "progress"].forEach((eventType) => {
        this.xmlhttp.addEventListener(eventType, this.onProgress);
      });
    }

    ["abort", "error"].forEach((eventType) => {
      this.xmlhttp.addEventListener(eventType, () => this.errorHandler(reject));
    });

    this.xmlhttp.addEventListener("timeout", () => this.timeoutHandler(reject));

    this.xmlhttp.addEventListener("load", () =>
      this.responseHandler(resolve, reject),
    );

    // Set custom events
    Object.keys(this.events).forEach((eventName) => {
      this.xmlhttp.addEventListener(eventName, this.events[eventName]);
    });
  }

  /**
   */
  openConnection() {
    this.xmlhttp.open(this.method, this.url, true);
  }

  /**
   */
  setBasicAuthentication() {
    if (Cache.hasItem("basic")) {
      const basicToken = Cache.getItem("basic");

      if (typeof basicToken === "string") {
        this.xmlhttp.setRequestHeader("Authorization", `Basic ${basicToken}`);
      }
    }
  }

  /**
   */
  getResponseHeader(headerName: string): string | null {
    return this.xmlhttp.getResponseHeader(headerName);
  }

  /**
   */
  setCustomHeaders() {
    Object.keys(this.headers).forEach((headerName) => {
      this.xmlhttp.setRequestHeader(headerName, this.headers[headerName]);
    });
  }

  /**
   */
  setOtherOptions() {
    if (
      this.options.withCredentials ||
      getSetting("XHR_ALWAYS_WITH_CREDENTIALS", Cache.hasItem("basic"))
    ) {
      this.xmlhttp.withCredentials = true;
    }
    this.xmlhttp.responseType = this.responseType;
    this.xmlhttp.timeout = this.timeout;
  }

  /**
   */
  sendData() {
    if (this.data == null) {
      this.xmlhttp.send();
    } else {
      const stringData = isPlainObject(this.data)
        ? JSON.stringify(this.data)
        : this.data;
      this.xmlhttp.send(stringData);
    }
  }

  /**
   */
  setXMLHttpRequest(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.xmlhttp = new XMLHttpRequest();

      this.setEventListeners(resolve, reject);

      this.openConnection();

      this.setBasicAuthentication();

      this.setCustomHeaders();

      this.setOtherOptions();

      this.sendData();
    });
  }

  /**
   */
  async fetch(): Promise<any> {
    try {
      return await this.setXMLHttpRequest();
    } catch (err) {
      if (err instanceof NetworkException && this.attempts < 2) {
        this.attempts += 1;
        return this.fetch();
      }

      throw err;
    }
  }
}

/**
 */
const validateInputArguments = (args: RequestOptions) => {
  if (args == null) {
    throw new IllegalArgumentException(
      "Missing request options, expecting an object as argument for xhr",
    );
  }

  if (!isPlainObject(args)) {
    throw new IllegalArgumentException(
      `Expecting an object as argument for xhr, but received: ${typeof args}`,
    );
  }

  if (!has(args, "url")) {
    throw new IllegalArgumentException(
      `No url property found in arguments of xhr, received: ${JSON.stringify(
        args,
      )}`,
    );
  }

  return true;
};

/**
 */
const xhr = (args: RequestOptions): Promise<any> => {
  validateInputArguments(args);

  return new XHR(args).fetch();
};

export default xhr;
